import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import './BOMItemNode.css';

const BOMItemNode = ({ data, selected }) => {
  const {
    itemCode,
    itemName,
    itemType,
    isPhantom,
    isNew,
    isTarget,
    hasModifiedBOM,
    hasChildren,
    childrenCount,
    isHighlighted,
    instanceOf,
    isPending,
    isModified,
    stockLevel,
  } = data;

  const getNodeClass = () => {
    const classes = ['bom-item-node'];
    
    if (isTarget) classes.push('target');
    else if (isPending) classes.push('pending');
    else if (isModified) classes.push('modified');
    else if (isPhantom) classes.push('phantom');
    else if (hasChildren) classes.push('subassembly');
    else classes.push('rawmaterial');
    
    if (selected) classes.push('selected');
    if (isNew) classes.push('is-new');
    if (hasModifiedBOM) classes.push('has-modified-bom');
    if (isHighlighted) classes.push('is-highlighted');
    if (instanceOf) classes.push('is-instance');
    if (isPending) classes.push('is-pending');
    if (isModified) classes.push('is-modified');
    
    return classes.join(' ');
  };

  const getTypeInfo = () => {
    if (isTarget) {
      return {
        label: 'TARGET',
        sublabel: isPending ? 'New Item' : 'Building This',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <circle cx="12" cy="12" r="6"/>
            <circle cx="12" cy="12" r="2"/>
          </svg>
        ),
        color: '#22c55e',
      };
    }
    if (isPending) {
      return {
        label: 'NEW ITEM',
        sublabel: 'Unsaved',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
        ),
        color: '#8b5cf6',
      };
    }
    if (isModified) {
      return {
        label: 'MODIFIED',
        sublabel: hasChildren ? 'Sub-Assembly' : (itemType === 'raw_material' ? 'Raw Material' : 'Component'),
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        ),
        color: '#60a5fa',
      };
    }
    if (isPhantom) {
      return {
        label: 'PHANTOM',
        sublabel: 'Pass-through',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
        ),
        color: '#f59e0b',
      };
    }
    if (hasChildren) {
      return {
        label: 'SUB-ASSEMBLY',
        sublabel: childrenCount ? `${childrenCount} component${childrenCount !== 1 ? 's' : ''}` : 'Has Components',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1"/>
            <rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="14" y="14" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/>
            <circle cx="12" cy="12" r="2" fill="currentColor"/>
          </svg>
        ),
        color: '#3b82f6',
      };
    }
    
    // Raw material / component (leaf node)
    if (itemType === 'raw_material') {
    return {
        label: 'RAW MATERIAL',
        sublabel: '',
        icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        </svg>
        ),
        color: '#64748b',
    };
    }

    return {
    label: 'COMPONENT',
    sublabel: '',
    icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="4"/>
        </svg>
    ),
    color: '#94a3b8',
    };
  };

  const typeInfo = getTypeInfo();

  return (
    <div className={getNodeClass()}>
      {/* Left Handle - for receiving connections (this is a child of something) */}
      {!isTarget && (
        <Handle
          type="target"
          position={Position.Left}
          id="left"
          className="handle handle-left"
        />
      )}

      {/* Type Header */}
      <div className="node-header" style={{ '--header-color': typeInfo.color }}>
        <div className="header-icon">
          {typeInfo.icon}
        </div>
        <div className="header-text">
          <span className="header-label">{typeInfo.label}</span>
          <span className="header-sublabel">{typeInfo.sublabel}</span>
        </div>
        {instanceOf && (
          <div className="instance-badge" title="Another instance of this item">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="8" y="8" width="12" height="12" rx="2"/>
              <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/>
            </svg>
          </div>
        )}
        {hasChildren && !isTarget && !isPending && !isModified && (
          <div className="children-badge" title={`${childrenCount || '?'} direct components`}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4L4 8v8l8 4 8-4V8l-8-4z"/>
            </svg>
            {childrenCount && <span>{childrenCount}</span>}
          </div>
        )}
      </div>

      {/* Node Content */}
      <div className="node-content">
        {/* Item Code */}
        <div className="item-code" title={itemCode}>
          {itemCode}
        </div>

        {/* Item Name */}
        <div className="item-name" title={itemName}>
          {itemName}
        </div>

        {/* Status Row */}
        <div className="status-row">
          {/* Item Type Badge */}
          <span className="item-type-badge" title={itemType}>
            {itemType === 'finished_good' ? 'FG' : 
             itemType === 'assembly' ? 'ASM' : 
             itemType === 'manufactured' ? 'MFG' : 
             itemType === 'raw_material' ? 'RAW' : 'OTH'}
          </span>
          
          {/* Stock Level */}
          {stockLevel !== undefined && !isPending && (
            <span className="stock-badge" title="Stock on hand">
              {stockLevel} in stock
            </span>
          )}
          
          {/* Phantom indicator */}
          {isPhantom && (
            <span className="phantom-tag" title="Components pass through">
              PHANTOM
            </span>
          )}

        </div>

        {/* Pending/Modified Notice */}
        {isPending && (
          <div className="save-notice pending-notice">
            Will be created on save
          </div>
        )}
        {isModified && !isPending && (
          <div className="save-notice modified-notice">
            Changes pending save
          </div>
        )}
        
        {/* Modified BOM indicator */}
        {hasModifiedBOM && !isPending && !isModified && (
          <div className="save-notice bom-notice">
            BOM modified
          </div>
        )}
      </div>

      {/* Right Handle - for creating connections (this is a parent of something) */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="handle handle-right"
      />
    </div>
  );
};

export default memo(BOMItemNode);